import { AxiosRequestConfig } from "axios";
import { Report, ITableReport } from "src/Interfaces/TableInterfaces";
import { OrderBook } from "src/Interfaces/orderInterfaces";
import { IOpenOrder } from "src/Interfaces/marketmaking";
import { Graph_Dto } from "src/Interfaces/orderbookGraphInterfaces";
import { DropdownItem } from "src/Interfaces/dropdownInterfaces";
import { deleteRequest, getRequest, getTable, postDownloadExcel, postRequest, putRequest } from "./requestTemplates";

export interface FuturesSettings {
  annualInterestRatePercent: number | null;
}

export interface BaseChoice { instrumentId: string; name: string }
export interface FuturesSearch { baseInstrumentId?: string; instrumentId?: string }
export interface FuturesWorkbookSearch { futureSearch: FuturesSearch; chainSearch: FuturesSearch }
export interface DomainRow extends ITableReport {
  baseInstrumentId: string;
  baseSymbol: string;
  maxOptionSpread: number | null;
  maxOptionSpreadPercent: number | null;
}
export interface DomainInput {
  baseInstrumentId: string;
  maxOptionSpread: number | null;
  maxOptionSpreadPercent: number | null;
}
export interface FuturePriceRow extends ITableReport {
  instrumentId: string;
  symbol: string;
  baseInstrumentId: string;
  baseSymbol: string;
  remainingTime: string | null;
  remainingDays: number | null;
  lastFuturePrice: number | null;
  futureTradeVolume: number | null;
  lastStockPrice: number | null;
  vwsp: number | null;
  fTheor: number | null;
  auto: number | null;
}
export interface SyntheticRow extends ITableReport {
  baseInstrumentId: string;
  futureInstrumentIds: string[];
  fSyn: number | null;
  vwsp: number | null;
  weight: number;
}

export const getSettings = () => getRequest<FuturesSettings>("/v1/futuresMarketMaking/settings");
export const saveSettings = (settings: FuturesSettings) =>
  putRequest<FuturesSettings, FuturesSettings>("/v1/futuresMarketMaking/settings", settings);
export const getBaseChoices = () => getRequest<BaseChoice[]>("/v1/futuresMarketMaking/baseChoices");
export const getDomainBaseChoices = () => getRequest<BaseChoice[]>("/v1/futuresMarketMaking/domainBaseChoices");
export const addDomain = (input: DomainInput) =>
  postRequest("/v1/futuresMarketMaking/domain", input);
export const editDomain = (id: number, input: DomainInput) =>
  putRequest(`/v1/futuresMarketMaking/domain/${id}`, input);
export const deleteDomain = (id: number) =>
  deleteRequest(`/v1/futuresMarketMaking/domain/${id}`);

const table = <T extends ITableReport>(url: string) =>
  (search: FuturesSearch, offset: number | null, pageSize: number | null,
    sortingType?: string, additional?: DropdownItem, options?: AxiosRequestConfig): Promise<Report<T>> =>
    getTable<FuturesSearch, T>(url, search, offset, pageSize, sortingType, additional, options);

export const getDomainTable = table<DomainRow>("/v1/futuresMarketMaking/domainTable");
export const getFuturesTable = table<FuturePriceRow>("/v1/futuresMarketMaking/futuresTable");
export const getSyntheticTable = table<SyntheticRow>("/v1/futuresMarketMaking/syntheticChainTable");
export const getDomainTableExcel = (search: FuturesSearch) =>
  postDownloadExcel("/v1/futuresMarketMaking/domainTableExcel", search);
export const getFuturesTableExcel = (search: FuturesSearch) =>
  postDownloadExcel("/v1/futuresMarketMaking/futuresTableExcel", search);
export const getSyntheticTableExcel = (search: FuturesSearch) =>
  postDownloadExcel("/v1/futuresMarketMaking/syntheticChainTableExcel", search);
export const getFuturesWorkbookExcel = (search: FuturesWorkbookSearch) =>
  postDownloadExcel("/v1/futuresMarketMaking/calculationsExcel", search);

export interface FuturesExecutionBaseSetting {
  baseInstrumentId: string;
  baseSymbol: string;
  firstStepTradeVolume: number | null;
  secondStepTradeVolume: number | null;
}

export interface FuturesExecutionSettings {
  referenceValidityMinutes: number | null;
  maxFairPriceChangePercent: number | null;
  minimumBuyingPower: number | null;
  removeExtraOrders: boolean;
  decreaseFirstStepPercent: number | null;
  decreaseSecondStepPercent: number | null;
  increaseFirstStepPercent: number | null;
  increaseSecondStepPercent: number | null;
  baseSettings: FuturesExecutionBaseSetting[];
}

export interface FuturesExecutionChoice {
  instrumentId: string;
  symbol: string;
  baseInstrumentId: string;
  baseSymbol: string;
}

export interface FuturesExecutionAccountChoice { id: number; name: string }

export interface FuturesExecutionTaskInput {
  futureInstrumentId: string;
  sahraUserId: number;
  minimumTriggerIntervalSeconds: number;
  referenceTolerancePercent: number | null;
  priceReference: "VWSP" | "FTheor" | "AutoSwitch";
  initialFactorMode: "Manual" | "Auto" | "Continues";
  initialFactor: number | null;
  orderType: "Open" | "Offset";
}

export interface FuturesExecutionTaskRow extends ITableReport, FuturesExecutionTaskInput {
  currentFactor: number | null;
  previousTradingFactor: number | null;
  autoRefill: { value: boolean; putUrlCmd: string };
  futureSymbol: string;
  futureName: string;
  futureTseLink: string;
  minPricePermit?: number | null;
  maxPricePermit?: number | null;
  baseInstrumentId: string;
  baseSymbol: string;
  accountName: string;
  isActive: boolean;
}

export interface FuturesExecutionTaskSearch {
  baseInstrumentId?: string;
  futureInstrumentId?: string;
  isActive?: boolean;
}

const executionUrl = "/v1/futuresMarketMaking/execution";
export const getFuturesExecutionSettings = () =>
  getRequest<FuturesExecutionSettings>(`${executionUrl}/settings`);
export const saveFuturesExecutionSettings = (input: FuturesExecutionSettings) =>
  putRequest<FuturesExecutionSettings, FuturesExecutionSettings>(`${executionUrl}/settings`, input);
export const getFuturesExecutionChoices = () =>
  getRequest<FuturesExecutionChoice[]>(`${executionUrl}/futureChoices`);
export const getFuturesExecutionAccounts = () =>
  getRequest<FuturesExecutionAccountChoice[]>(`${executionUrl}/accountChoices`);
export const getFuturesExecutionTasks = (
  search: FuturesExecutionTaskSearch, offset: number | null, pageSize: number | null,
  sortingType?: string, additional?: DropdownItem, options?: AxiosRequestConfig,
): Promise<Report<FuturesExecutionTaskRow>> =>
  getTable<FuturesExecutionTaskSearch, FuturesExecutionTaskRow>(
    `${executionUrl}/tasks/table`, search, offset, pageSize, sortingType, additional, options,
  );
export const getFuturesExecutionNetVolumeChart = (taskId: number): Promise<Graph_Dto[]> =>
  getRequest(`${executionUrl}/tasks/${taskId}/netVolumeChart`);
export const getFuturesExecutionNetVolume2Chart = (taskId: number): Promise<Graph_Dto[]> =>
  getRequest(`${executionUrl}/tasks/${taskId}/netVolume2Chart`);
export const getFuturesExecutionCurrentFactorChart = (taskId: number): Promise<Graph_Dto[]> =>
  getRequest(`${executionUrl}/tasks/${taskId}/currentFactorChart`);
export const addFuturesExecutionTask = (input: FuturesExecutionTaskInput) =>
  postRequest<FuturesExecutionTaskInput, number>(`${executionUrl}/tasks`, input);
export const editFuturesExecutionTask = (id: number, input: FuturesExecutionTaskInput) =>
  putRequest<FuturesExecutionTaskInput, void>(`${executionUrl}/tasks/${id}`, input);
export type FuturesExecutionTaskOperation = "play" | "pause" | "resend" | "delete";
export interface FuturesExecutionTaskOperationResult {
  changed: number;
  notAllowed: number;
  unready: number;
  dispatchFailed: number;
  fairPriceChangeRejected: number;
  confirmationRequired: boolean;
  remainingTriggerIntervalSeconds: number;
}
export interface FuturesExecutionOfferPreviewRow {
  rowNo: number;
  from: number;
  to: number;
  calculatedVolume: number;
  finalVolume: number | null;
  calculatedPrice: number | null;
  flexibilityAmount: number;
  flexibilityPercent: number;
  disabledByBuyingPower: boolean;
  disabledByPriceLimit: boolean;
}
export interface FuturesExecutionOfferPreview {
  taskId: number;
  snapshotId: number | null;
  programId: number | null;
  factorSnapshotId: number | null;
  futureInstrumentId: string;
  account: string;
  orderType: string;
  referencePrice: number | null;
  factor: number | null;
  fairPrice: number | null;
  lastSuccessfulFairPrice: number | null;
  fairPriceChangePercent: number | null;
  maxFairPriceChangePercent: number | null;
  fairPriceChangeExceeded: boolean;
  minPricePermit: number | null;
  maxPricePermit: number | null;
  snapshotAt: string | null;
  minimumBuyingPower: number | null;
  isBuyingPowerLimited: boolean;
  buy: FuturesExecutionOfferPreviewRow[];
  sell: FuturesExecutionOfferPreviewRow[];
}
export const getFuturesExecutionPlayPreviews = (ids: number[]) =>
  postRequest<{ ids: number[] }, FuturesExecutionOfferPreview[]>(
    `${executionUrl}/tasks/preview`, { ids });
export interface FuturesExecutionPreviewVersion {
  snapshotId: number;
  programId: number;
  factorSnapshotId: number | null;
}
export const changeFuturesExecutionTask = (id: number, action: FuturesExecutionTaskOperation,
  preview?: FuturesExecutionPreviewVersion, overrideMinimumTriggerInterval = false) =>
  postRequest<FuturesExecutionPreviewVersion & { overrideMinimumTriggerInterval: boolean }, FuturesExecutionTaskOperationResult>(
    `${executionUrl}/tasks/${id}/${action}`,
    { snapshotId: preview?.snapshotId ?? 0, programId: preview?.programId ?? 0,
      factorSnapshotId: preview?.factorSnapshotId ?? null, overrideMinimumTriggerInterval });
export const changeFuturesExecutionTasks = (ids: number[], action: FuturesExecutionTaskOperation,
  previews?: Record<number, FuturesExecutionPreviewVersion>, overrideMinimumTriggerInterval = false) =>
  postRequest<{ ids: number[]; previews?: Record<number, FuturesExecutionPreviewVersion>;
    overrideMinimumTriggerInterval: boolean }, FuturesExecutionTaskOperationResult>(
    `${executionUrl}/tasks/bulk/${action}`, { ids, previews, overrideMinimumTriggerInterval });
export interface FuturesExecutionOrderBookResponse {
  fullOrderBook: OrderBook | null;
  instrumentStatistic: {
    minPricePermit: number;
    maxPricePermit: number;
    priceYesterday: number | null;
    priceMin: number | null;
    priceMax: number | null;
    final: { price: number } | null;
    trade: { price: number } | null;
  } | null;
  liveDetails: FuturesExecutionLiveDetails | null;
  openOrders: IOpenOrder[];
}
export const getFuturesExecutionOrderBook = (taskId: string, signal?: AbortSignal) =>
  getRequest<FuturesExecutionOrderBookResponse | OrderBook | null>(
    `${executionUrl}/tasks/${encodeURIComponent(taskId)}/orderBook`, { signal });

export interface FuturesExecutionMarketDataResponse {
  instrumentStatistic: FuturesExecutionOrderBookResponse["instrumentStatistic"];
  liveDetails: FuturesExecutionLiveDetails | null;
}
export const getFuturesExecutionMarketData = (taskId: string, signal?: AbortSignal) =>
  getRequest<FuturesExecutionMarketDataResponse>(
    `${executionUrl}/tasks/${encodeURIComponent(taskId)}/marketData`, { signal });


export const getFuturesExecutionOpenOrders = (taskId: string, signal?: AbortSignal) =>
  getRequest<IOpenOrder[]>(`${executionUrl}/tasks/${encodeURIComponent(taskId)}/openOrders`, { signal });

export interface FuturesExecutionLiveDetails {
  baseInsPriceLive: number | null;
  baseInsPriceYesterday?: number | null;
  liveFairPrice: number | null;
  sentFairPrice?: number | null;
  vwsp: number | null;
  fTheor: number | null;
  lastFactorTriggerAt: string | null;
  lastFuturePrice: number | null;
}
export const getFuturesExecutionLiveDetails = (taskId: string, signal?: AbortSignal) =>
  getRequest<FuturesExecutionLiveDetails>(
    `${executionUrl}/tasks/${encodeURIComponent(taskId)}/liveDetails`, { signal });

export interface FuturesExecutionProgramRow {
  rowNo: number;
  from: number;
  to: number;
  flexibilityAmount: number;
  flexibilityPercent: number;
}
export interface FuturesExecutionTrigger {
  id: number;
  triggerType: string;
  status: string;
  referencePrice: number | null;
  fairPrice: number | null;
  factor: number | null;
  createdAt: string;
}
export const getFuturesExecutionTriggers = (taskId: number) =>
  getRequest<FuturesExecutionTrigger[]>(`${executionUrl}/tasks/${taskId}/triggers`);
export interface FuturesExecutionTriggerRow extends ITableReport {
  createdAt: string;
  triggerType: string;
  isSuccess: boolean;
  status: string;
  changeBasePricePercent: number | null;
  vwsp: number | null;
  fTheor: number | null;
  lastStockPrice: number | null;
  priceReference: string | null;
  effectivePriceReference: string | null;
  priceReferenceDisplay: string | null;
  referencePrice: number | null;
  factor: number | null;
  fairPrice: number | null;
  orderCount: number;
}
export const getFuturesExecutionTriggerTable = (
  search: FuturesExecutionDetailSearch, offset: number | null, pageSize: number | null,
  sortingType?: string,
): Promise<Report<FuturesExecutionTriggerRow>> =>
  getTable<FuturesExecutionDetailSearch, FuturesExecutionTriggerRow>(
    `${executionUrl}/tasks/${search.taskId}/triggers/table`, search, offset, pageSize, sortingType);
export interface FuturesExecutionProgram {
  buy: FuturesExecutionProgramRow[];
  sell: FuturesExecutionProgramRow[];
}
export interface FuturesExecutionPredefinedProgram extends FuturesExecutionProgram {
  id: number;
  name: string;
}
export interface FuturesExecutionDetailSearch { taskId?: number }
export interface FuturesExecutionOrderRow extends ITableReport {
  futureInstrumentId: string;
  accountName: string;
  side: string;
  programRowNumber: number;
  orderPrice: number;
  executedPrice: number | null;
  createdAt: string;
  updatedAt: string;
  filledVolume: number;
  totalVolume: number;
  pendingVolume: number;
  isActive: boolean;
  message: string | null;
}
export interface FuturesExecutionTradeRow extends ITableReport {
  futureInstrumentId: string;
  accountName: string;
  side: string;
  price: number;
  volume: number;
  executedAt: string;
}
export interface FuturesExecutionErrorRow extends ITableReport {
  futureInstrumentId: string;
  accountName: string;
  side: string;
  volume: number;
  price: number;
  message: string;
  code: string | null;
  createdAt: string;
}

export const getFuturesExecutionProgram = (taskId: number) =>
  getRequest<FuturesExecutionProgram>(`${executionUrl}/tasks/${taskId}/program`);
export const saveFuturesExecutionProgram = (taskId: number, input: FuturesExecutionProgram) =>
  putRequest<FuturesExecutionProgram, FuturesExecutionProgram>(`${executionUrl}/tasks/${taskId}/program`, input);
export const getFuturesExecutionPredefinedPrograms = () =>
  getRequest<FuturesExecutionPredefinedProgram[]>(`${executionUrl}/predefinedPrograms`);
export const saveFuturesExecutionPredefinedProgram = (input: Omit<FuturesExecutionPredefinedProgram, "id">) =>
  postRequest<Omit<FuturesExecutionPredefinedProgram, "id">, FuturesExecutionPredefinedProgram>(
    `${executionUrl}/predefinedPrograms`, input);
export const deleteFuturesExecutionPredefinedProgram = (id: number) =>
  deleteRequest(`${executionUrl}/predefinedPrograms/${id}`);

export const getFuturesExecutionOrders = (
  search: FuturesExecutionDetailSearch, offset: number | null, pageSize: number | null,
  sortingType?: string, additional?: DropdownItem, options?: AxiosRequestConfig,
): Promise<Report<FuturesExecutionOrderRow>> =>
  getTable<FuturesExecutionDetailSearch, FuturesExecutionOrderRow>(
    search.taskId == null ? `${executionUrl}/orders/table` : `${executionUrl}/tasks/${search.taskId}/orders/table`,
    search, offset, pageSize, sortingType, additional, options);
export const cancelFuturesExecutionOrder = (orderId: number) =>
  postRequest<{}, unknown>(`${executionUrl}/orders/${orderId}/cancel`, {});
export const getFuturesExecutionTrades = (
  search: FuturesExecutionDetailSearch, offset: number | null, pageSize: number | null,
  sortingType?: string, additional?: DropdownItem, options?: AxiosRequestConfig,
): Promise<Report<FuturesExecutionTradeRow>> =>
  getTable<FuturesExecutionDetailSearch, FuturesExecutionTradeRow>(
    search.taskId == null ? `${executionUrl}/trades/table` : `${executionUrl}/tasks/${search.taskId}/trades/table`,
    search, offset, pageSize, sortingType, additional, options);
export const getFuturesExecutionErrors = (
  search: FuturesExecutionDetailSearch, offset: number | null, pageSize: number | null,
  sortingType?: string, additional?: DropdownItem, options?: AxiosRequestConfig,
): Promise<Report<FuturesExecutionErrorRow>> =>
  getTable<FuturesExecutionDetailSearch, FuturesExecutionErrorRow>(
    search.taskId == null ? `${executionUrl}/errors/table` : `${executionUrl}/tasks/${search.taskId}/errors/table`,
    search, offset, pageSize, sortingType, additional, options);
